import React, { FunctionComponent, useEffect, useRef } from "react";
import Avatar from "@material-ui/core/Avatar";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { googleLogin } from "../../actions/loginActions";

const useStyles = makeStyles(() => ({
  card: {
    background: "#ffffff",
    borderRadius: 24,
    padding: "3rem 2.75rem",
    width: "100%",
    maxWidth: 460,
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    boxSizing: "border-box",
  },
  avatar: {
    width: 72,
    height: 72,
    marginBottom: "1.25rem",
    background: "#8e24aa",
    "& svg": {
      fontSize: 34,
    },
  },
  heading: {
    fontWeight: 700,
    color: "#111827",
    marginBottom: "1rem",
  },
  subtitle: {
    color: "#4b5563",
    lineHeight: 1.5,
    maxWidth: 360,
    marginBottom: "2rem",
  },
  googleWrap: {
    display: "flex",
    justifyContent: "center",
    minHeight: 44,
    width: "100%",
  },
  footer: {
    color: "#9ca3af",
    marginTop: "1.75rem",
    lineHeight: 1.5,
    maxWidth: 340,
  },
}));

type LoginFormProps = {
  // Kept for compatibility with the login container; unused in Google-only mode.
  handleForm?: any;
  loading?: boolean;
  loadingOTP?: boolean;
  otpStep?: boolean;
  otp?: string;
  setOtp?: (value: string) => void;
  phoneNumber?: string;
  otpLoading?: boolean;
  onVerifyOtp?: () => void;
  onResendOtp?: () => void;
  onBack?: () => void;
}

// OAuth 2.0 Web client ID — must match the backend's GOOGLE_SSO_CLIENT_ID.
const GOOGLE_SSO_CLIENT_ID = process.env.REACT_APP_GOOGLE_SSO_CLIENT_ID;

const LoginForm: FunctionComponent<LoginFormProps> = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const googleBtnRef = useRef<HTMLDivElement>(null);

  const handleGoogleCredential = async (credential: string) => {
    try {
      await dispatch(googleLogin(credential));
      history.push("/dashboard");
    } catch (error) {
      // error snackbar is raised by the googleLogin action
    }
  };

  useEffect(() => {
    if (!GOOGLE_SSO_CLIENT_ID) return;
    const renderGoogleButton = () => {
      const google = (window as any).google;
      if (!google?.accounts?.id || !googleBtnRef.current) return;
      google.accounts.id.initialize({
        client_id: GOOGLE_SSO_CLIENT_ID,
        callback: (response: any) => {
          if (response?.credential) handleGoogleCredential(response.credential);
        },
      });
      google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        width: 320,
        text: "signin_with",
      });
    };
    if ((window as any).google?.accounts?.id) {
      renderGoogleButton();
      return;
    }
    const scriptId = "google-gsi-client";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", renderGoogleButton);
    return () => script?.removeEventListener("load", renderGoogleButton);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container component="main" maxWidth="xs" disableGutters className={classes.card}>
      <Avatar className={classes.avatar}>
        <LockOutlinedIcon />
      </Avatar>
      <Typography component="h1" variant="h4" className={classes.heading}>
        Welcome Back
      </Typography>
      <Typography variant="body1" className={classes.subtitle}>
        Sign in with your Redcliffe Labs Google account to access the Offline
        Support CRM.
      </Typography>
      {GOOGLE_SSO_CLIENT_ID ? (
        <div ref={googleBtnRef} className={classes.googleWrap} />
      ) : (
        <Typography
          variant="body2"
          style={{ color: "#d32f2f" }}
        >
          Google sign-in is not configured. Please set
          REACT_APP_GOOGLE_SSO_CLIENT_ID.
        </Typography>
      )}
      <Typography variant="body2" className={classes.footer}>
        Access is restricted to authorized Redcliffe Labs accounts.
      </Typography>
    </Container>
  );
}

export default LoginForm


// ===========================================================================
// OLD username / password / OTP login form — kept for reference (disabled).
// The active component above is Google-only. To restore this flow, replace
// the component above with the code below and re-wire the props in
// containers/login/index.tsx.
// ===========================================================================
// import React, { FunctionComponent, useState } from "react";
// import Avatar from "@material-ui/core/Avatar";
// import Button from '@mui/material/Button';
// import TextField from "@material-ui/core/TextField";
// import CircularProgress from '@material-ui/core/CircularProgress';
// import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
// import Typography from "@material-ui/core/Typography";
// import { makeStyles } from "@material-ui/core/styles";
// import Container from "@material-ui/core/Container";
// import { useForm } from "react-hook-form";
// import { useHistory } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import { resetPassword, resetPasswordReset, sendOtp } from "../../actions/loginActions";
// import { useEffect } from "react";
// import { InputAdornment } from "@material-ui/core";
// import VisibilityIcon from '@mui/icons-material/Visibility';
// import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
// 
// const useStyles = makeStyles(theme => ({
//   paper: {
//     
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     width:"100%",
//     height:"100%"
//   },
//   loginForm:{
//     border: "1px solid gainsboro",
//     background:" #fff",
//     padding: "2rem",
//     borderRadius: "5px",
//     position: "absolute",
//     top:" 20%",
//     right: "7%",
//     height:" 30rem",
//   },
//     
//   avatar: {
//     margin: theme.spacing(1),
//     backgroundColor: theme.palette.secondary.main
//   },
//   form: {
//     width: "100%", // Fix IE 11 issue.
//     marginTop: theme.spacing(1),
//     height: "100%",
//     display: "flex",
//     flexDirection: "column",
//     justifyContent: "space-around",
//   },
//   wrapper: {
//     // margin: theme.spacing(1),
//     position: 'relative',
//   },
//   buttonProgress: {
//     position: 'absolute',
//     top: '50%',
//     left: '50%',
//     marginTop: -9,
//     marginLeft: -9,
//   },
//   submit: {
//     margin: theme.spacing(3, 0, 2)
//   }
// }));
// 
// type FormData = {
//   email: string;
//   password: string;
// }
// 
// type LoginFormProps = {
//   handleForm: any;
//   loading: boolean;
//   loadingOTP: boolean;
//   // OTP step (restricted usergroups only) — optional so other callers are unaffected.
//   otpStep?: boolean;
//   otp?: string;
//   setOtp?: (value: string) => void;
//   phoneNumber?: string;
//   otpLoading?: boolean;
//   onVerifyOtp?: () => void;
//   onResendOtp?: () => void;
//   onBack?: () => void;
// }
// 
// const LoginForm: FunctionComponent<LoginFormProps> = ({
//   handleForm,
//   loading,
//   loadingOTP,
//   otpStep = false,
//   otp: otpCode = "",
//   setOtp: setOtpCode,
//   phoneNumber = "",
//   otpLoading = false,
//   onVerifyOtp,
//   onResendOtp,
//   onBack,
// }) => {
//   const classes = useStyles();
//   const { handleSubmit, register, errors } = useForm<FormData>();
// 
//   const history = useHistory()
// 
//   const dispatch = useDispatch()
// 
//   const loginReducer = useSelector((state: any) => state.loginReducer)
//   const { otp: otpSent, success, loading: loadingReset, loadingOTP: loadingResetOTP } = loginReducer
// 
//   const [forgot, setForgot] = useState<any>(false)
// 
//   const [email, setEmail] = useState("")
//   const [password, setPassword] = useState("")
//   const [otp, setOtp] = useState("");
//   const [showPassword, setShowPassword] = useState<boolean>(false);
// 
//   const sendOtpHandler = () => {
//     dispatch(sendOtp({ email }))
//   }
// 
//   const resetPasswordHandler = async () => {
//     const body: any = {
//       token: otp,
//       password
//     }
//     if (otp && password) {
//       dispatch(resetPassword(body))
//     }
//   }
// 
//   useEffect(() => {
//     if (success) {
//       history.push('/')
//     }
//   }, [history, success])
// 
//   useEffect(() => {
//     dispatch(resetPasswordReset())
//   }, [dispatch])
// 
//   const resetDetails = () => {
//     setForgot(false)
//     setEmail("")
//     setOtp("")
//     setPassword("")
//     dispatch(resetPasswordReset())
//   }
// 
//   return (
//     <Container component="main" maxWidth="xs" className={classes.loginForm}>
//       <div className={classes.paper}>
//         <Avatar className={classes.avatar} style={{ background: "purple" }}>
//           <LockOutlinedIcon />
//         </Avatar>
//         <Typography component="h1" variant="h5">
//           {otpStep ? "Verify OTP" : forgot ? "Reset Password" : "Login"}
//         </Typography>
//         {otpStep ? (
//           <div className={classes.form}>
//             <Typography
//               variant="body2"
//               style={{ textAlign: "center", margin: "1rem 0", color: "#555" }}
//             >
//               {phoneNumber
//                 ? `An OTP has been sent to your registered mobile number ${"*".repeat(
//                     Math.max(String(phoneNumber).length - 2, 0)
//                   )}${String(phoneNumber).slice(-2)}.`
//                 : "An OTP has been sent to your registered mobile number."}
//             </Typography>
//             <TextField
//               className="input"
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               id="login-otp"
//               label="Enter OTP"
//               autoFocus
//               value={otpCode}
//               inputProps={{ maxLength: 6, inputMode: "numeric" }}
//               onChange={(e) => {
//                 const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
//                 setOtpCode && setOtpCode(digits);
//               }}
//               onKeyDown={(e) => {
//                 if (
//                   e.key === "Enter" &&
//                   otpCode.length === 6 &&
//                   !otpLoading &&
//                   onVerifyOtp
//                 ) {
//                   onVerifyOtp();
//                 }
//               }}
//             />
//             <div className={classes.wrapper} style={{ marginTop: "1rem" }}>
//               <Button
//                 fullWidth
//                 variant="contained"
//                 color="primary"
//                 disabled={otpLoading || otpCode.length !== 6}
//                 className={classes.submit}
//                 onClick={() => onVerifyOtp && onVerifyOtp()}
//               >
//                 Verify &amp; Continue
//               </Button>
//               {otpLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
//             </div>
//             <div className={classes.wrapper} style={{ marginTop: "1rem" }}>
//               <Button
//                 fullWidth
//                 variant="text"
//                 color="primary"
//                 disabled={otpLoading}
//                 onClick={() => onResendOtp && onResendOtp()}
//               >
//                 Resend OTP
//               </Button>
//             </div>
//             <div className={classes.wrapper}>
//               <Button
//                 fullWidth
//                 variant="text"
//                 color="secondary"
//                 disabled={otpLoading}
//                 onClick={() => onBack && onBack()}
//               >
//                 Back
//               </Button>
//             </div>
//           </div>
//         ) : !forgot ? (
//           <form
//             className={classes.form}
//             onSubmit={handleSubmit(handleForm)}
//             noValidate
//           >
//             <TextField
//               className="input"
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               id="email"
//               label="Email Address"
//               name="email"
//               autoComplete="email"
//               autoFocus
//               inputRef={register({
//                 required: "Required",
//                 // pattern: {
//                 //   value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
//                 //   message: "invalid email address"
//                 // }
//               })}
//               required={errors.email ? true : false}
//               error={errors.email ? true : false}
//               helperText={errors.email && errors.email.message}
//             />
//             <TextField
//               className="input"
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               name="password"
//               label="Password"
//               type={showPassword ? "text" : "password"}
//               id="password"
//               autoComplete="current-password"
//               inputRef={register({
//                 required: "Required"
//               })}
//               required={errors.password ? true : false}
//               error={errors.password ? true : false}
//               helperText={errors.password && errors.password.message}
//               InputProps={{
//                 endAdornment: (
//                   <div onClick={() => setShowPassword((previousState: any) => !previousState)}>
//                     <InputAdornment position="end">
//                         {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
//                     </InputAdornment>
//                   </div>
//                 )
//               }}
//             />
//             <div className={classes.wrapper} style={{marginTop:"1rem"}}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 color="primary"
//                 disabled={loading}
//                 className={classes.submit}
//               >
//                 Sign In
//               </Button>
//               {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
//             </div>
//             <div className={classes.wrapper}  style={{marginTop:"1rem"}}>
//               <Button
//                 fullWidth
//                 variant="contained"
//                 color="secondary"
//                 disabled={loading}
//                 className={classes.submit}
//                 onClick={() => setForgot(true)}
//               >
//                 Forgot Password
//               </Button>
//               {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
//             </div>
//           </form>
//         ) : (
//           <>
//             <div className={classes.form}>
//               <input
//                 className="input"
//                 name="email"
//                 type="email"
//                 placeholder="Email"
//                 value={email}
//                 onChange={(e) => setEmail(e.target.value)}
//                 style={{ width: "100%", margin: "1rem auto", padding: "14px 14px" }}
//               />
//               <div className={classes.wrapper}>
//                 <Button
//                   fullWidth
//                   variant="contained"
//                   color="primary"
//                   disabled={loadingResetOTP || !email}
//                   className={classes.submit}
//                   onClick={() => sendOtpHandler()}
//                 >
//                   Send OTP
//                 </Button>
//                 {loadingResetOTP && <CircularProgress size={24} className={classes.buttonProgress} />}
//               </div>
//               <input
//                 className="input"
//                 name="password"
//                 type="password"
//                 placeholder="Password"
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 style={{ width: "100%", margin: "1rem auto", padding: "14px 14px" }}
//                 disabled={!(otpSent && otpSent.status)}
//               />
//               <input
//                 className="input"
//                 name="otp"
//                 type="password"
//                 placeholder="OTP"
//                 value={otp}
//                 onChange={(e) => setOtp(e.target.value)}
//                 style={{ width: "100%", margin: "1rem auto", padding: "14px 14px" }}
//                 disabled={!(otpSent && otpSent.status)}
//               />
//               <div className={classes.wrapper}>
//                 <Button
//                   fullWidth
//                   variant="contained"
//                   color="primary"
//                   disabled={loadingReset || !(otpSent && otpSent.status) || !otp || !password}
//                   className={classes.submit}
//                   onClick={() => resetPasswordHandler()}
//                 >
//                   Update Password
//                 </Button>
//                 {loadingReset && <CircularProgress size={24} className={classes.buttonProgress} />}
//               </div>
//               <div className={classes.wrapper}>
//                 <Button
//                   fullWidth
//                   variant="contained"
//                   color="secondary"
//                   className={classes.submit}
//                   onClick={() => resetDetails()}
//                 >
//                   Login
//                 </Button>
//               </div>
//             </div>
//           </>
//         )}
//       </div>
//     </Container>
//   );
// }
// 
// export default LoginForm
